import { createParser, type EventSourceMessage } from 'eventsource-parser';

import { isRecord } from '@/lib/utils/type-guards';

type InsightStatusPayload = Record<string, unknown>;
type InsightTokenPayload = { delta?: string; text?: string };
type InsightDonePayload = { text?: string };
type InsightErrorPayload = { message?: string };

export interface TweetInsightStreamCallbacks {
  onComplete?: (finalText: string) => void;
  onDone?: (payload: InsightStatusPayload, finalText: string) => void;
  onError?: (message: string, aggregated: string) => void;
  onStart?: () => void;
  onStatus?: (payload: InsightStatusPayload) => void;
  onToken?: (payload: { aggregated: string; delta?: string; text?: string }) => void;
}

export interface TweetInsightStreamOptions {
  callbacks: TweetInsightStreamCallbacks;
  endpoint: string;
  errorMessage: string;
  requestBody: Record<string, unknown>;
  signal: AbortSignal;
}

const getPlainRecord = <T extends Record<string, unknown>>(value: unknown): T | null =>
  isRecord<T>(value) ? value : null;

const parseTokenPayload = (value: unknown): InsightTokenPayload | null => {
  const record = getPlainRecord(value);
  if (!record) {
    return null;
  }
  const payload: InsightTokenPayload = {};
  // biome-ignore lint/complexity/useLiteralKeys: requires indexed access for strict TS.
  const delta = record['delta'];
  if (typeof delta === 'string') {
    payload.delta = delta;
  }
  // biome-ignore lint/complexity/useLiteralKeys: requires indexed access for strict TS.
  const text = record['text'];
  if (typeof text === 'string') {
    payload.text = text;
  }
  return payload;
};

const parseDonePayload = (value: unknown): InsightDonePayload | null => {
  const record = getPlainRecord(value);
  if (!record) {
    return null;
  }
  const payload: InsightDonePayload = {};
  // biome-ignore lint/complexity/useLiteralKeys: requires indexed access for strict TS.
  const text = record['text'];
  if (typeof text === 'string') {
    payload.text = text;
  }
  return payload;
};

const parseErrorPayload = (value: unknown): InsightErrorPayload | null => {
  const record = getPlainRecord(value);
  if (!record) {
    return null;
  }
  const payload: InsightErrorPayload = {};
  // biome-ignore lint/complexity/useLiteralKeys: requires indexed access for strict TS.
  const message = record['message'];
  if (typeof message === 'string') {
    payload.message = message;
  }
  return payload;
};

const parseStatusPayload = (value: unknown): InsightStatusPayload | null => getPlainRecord(value);

export async function streamTweetInsights({
  endpoint,
  requestBody,
  signal,
  errorMessage,
  callbacks,
}: TweetInsightStreamOptions): Promise<void> {
  callbacks.onStart?.();

  const response = await fetch(endpoint, {
    method: 'POST',
    signal,
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const fallbackMessage = await response.text().catch(() => errorMessage);
    throw new Error(fallbackMessage || errorMessage);
  }
  if (!response.body) {
    throw new Error(errorMessage);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let aggregated = '';
  let streamErrored = false;

  const recordStreamError = (message: string) => {
    streamErrored = true;
    callbacks.onError?.(message, aggregated);
  };

  const applyToken = (payload: InsightTokenPayload) => {
    if (typeof payload.delta === 'string') {
      aggregated += payload.delta;
    } else if (typeof payload.text === 'string' && payload.text.length >= aggregated.length) {
      aggregated = payload.text;
    }

    callbacks.onToken?.({ aggregated, ...payload });
  };

  const applyCompletion = (payload: InsightDonePayload | null, statusPayload: InsightStatusPayload | null) => {
    const finalText = payload?.text && payload.text.length > 0 ? payload.text : aggregated;
    aggregated = finalText;
    callbacks.onDone?.(statusPayload ?? {}, finalText);
    callbacks.onComplete?.(finalText);
  };

  const processEvent = (event: EventSourceMessage) => {
    if (!event.data) {
      return;
    }

    try {
      const rawPayload: unknown = JSON.parse(event.data);
      const statusPayload = parseStatusPayload(rawPayload);

      switch (event.event) {
        case undefined: {
          break;
        }
        case 'status': {
          if (statusPayload) {
            callbacks.onStatus?.(statusPayload);
          }

          break;
        }
        case 'token': {
          const tokenPayload = parseTokenPayload(rawPayload);
          if (!tokenPayload) {
            return;
          }
          applyToken(tokenPayload);

          break;
        }
        case 'done': {
          const donePayload = parseDonePayload(rawPayload);
          applyCompletion(donePayload, statusPayload);

          break;
        }
        case 'error': {
          const errorPayload = parseErrorPayload(rawPayload);
          const message =
            errorPayload?.message && errorPayload.message.length > 0 ? errorPayload.message : errorMessage;
          recordStreamError(message);

          break;
        }
        default: {
          break;
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : errorMessage;
      recordStreamError(message);
    }
  };

  const parser = createParser({ onEvent: processEvent });

  const pump = async (): Promise<void> => {
    const { done, value } = await reader.read();
    if (done) {
      return;
    }

    parser.feed(decoder.decode(value, { stream: true }));
    await pump();
  };

  await pump();

  if (!streamErrored && aggregated.length === 0) {
    callbacks.onComplete?.('');
  }
}
